import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Components } from './styled';
import ProjectContainer from '../../components/containers/ProjectContainer';
import ProjectTableHeader from '../../components/ProjectTableHeader';
import { Lesson } from '../../api/types/lesson';
import { getAllLessons, updateLesson } from '../../api/lesson';
import { isProjectError } from '../../utils/errors';
import { normalizeLrc } from '../../utils/transcript/lrc';
import {
  useTranscriber,
  TranscriberStage,
  DownloadProgressState,
  TranscribeProgressState,
} from '../../hooks/useTranscriber';
import {
  formatDurationSeconds,
  formatEtaSeconds,
  formatMegabytes,
  formatPercent,
} from '../../utils/transcript/progressFormat';
import useFormTextState from '../../hooks/useFormTextState';
import useModalState from '../../hooks/useModalState';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import { ToastContext } from '../../providers/ToastProvider/context';
import TranscriptReviewModal, { TranscriptReviewModalState } from '../../components/modals/TranscriptReviewModal';
import useBatchTranscription, { BatchRowState } from './useBatchTranscription';
import { useBeforeUnloadWarning } from './useBeforeUnloadWarning';
import { isTranscriptCandidate } from './utils';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 25;

interface ReviewEntry {
  lesson: Lesson;
  lrc: string;
  savedAt: number;
  durationMs?: number;
}

const STAGE_LABELS: Record<TranscriberStage, string> = {
  /* eslint-disable @typescript-eslint/naming-convention */
  'idle': 'Idle',
  'loading-model': 'Loading model…',
  'downloading-audio': 'Downloading audio…',
  'decoding': 'Decoding audio…',
  'transcribing': 'Transcribing…',
  'done': 'Done',
  'error': 'Error',
  /* eslint-enable @typescript-eslint/naming-convention */
};

const lineCount = (lrc: string | undefined) => (lrc?.trim()?.length ? lrc.trim().split('\n').length : 0);

interface RowDisplay {
  label: string;
  secondary?: string;
  tone: 'default' | 'progress' | 'success' | 'error' | 'muted';
  progressPercent?: number;
}

/**
 * Builds the label/secondary-text/tone shown in a batch row's Status cell,
 * covering the active row's live download/transcribe progress, the
 * next-queued row's prefetch progress, and terminal states.
 */
const buildRowDisplay = (params: {
  rowState?: BatchRowState;
  isActiveRow: boolean;
  isPrefetchRow: boolean;
  prefetchPercent?: number;
  activeStage?: TranscriberStage;
  modelLoadProgress: number | null;
  downloadProgress: DownloadProgressState | null;
  transcribeProgress: TranscribeProgressState | null;
}): RowDisplay | undefined => {
  const {
    rowState,
    isActiveRow,
    isPrefetchRow,
    prefetchPercent,
    activeStage,
    modelLoadProgress,
    downloadProgress,
    transcribeProgress,
  } = params;

  if (isActiveRow && rowState) {
    if (activeStage === 'transcribing' && transcribeProgress) {
      const eta = formatEtaSeconds(transcribeProgress.etaSeconds);
      const percentLabel = `${transcribeProgress.estimated ? '~' : ''}${formatPercent(transcribeProgress.percent)}`;
      return {
        label: `Transcribing ${percentLabel}${eta ? ` · ${eta}` : ''}`,
        tone: 'progress',
        progressPercent: transcribeProgress.percent,
      };
    }
    if (activeStage === 'loading-model' && rowState.audioReady) {
      const pct = modelLoadProgress ?? 0;
      return { label: `Waiting for model ${formatPercent(pct)}`, tone: 'progress', progressPercent: pct };
    }
    if (activeStage === 'downloading-audio' || rowState.status === 'downloading') {
      if (rowState.audioReady) {
        return { label: 'Audio ready', tone: 'progress', progressPercent: 100 };
      }
      if (downloadProgress) {
        const eta = formatEtaSeconds(downloadProgress.etaSeconds);
        const secondary =
          downloadProgress.totalBytes > 0
            ? `${formatMegabytes(downloadProgress.loadedBytes)} / ${formatMegabytes(downloadProgress.totalBytes)}`
            : undefined;
        return {
          label: `Downloading ${formatPercent(downloadProgress.percent)}${eta ? ` · ${eta}` : ''}`,
          secondary,
          tone: 'progress',
          progressPercent: downloadProgress.percent,
        };
      }
      return { label: 'Downloading…', tone: 'progress' };
    }
  }

  if (isPrefetchRow && !rowState) {
    const pct = prefetchPercent ?? 0;
    if (pct >= 100) {
      return { label: 'Audio ready', tone: 'default', progressPercent: 100 };
    }
    return { label: `Pre-downloading ${formatPercent(pct)}`, tone: 'default', progressPercent: pct };
  }

  if (!rowState) {
    return undefined;
  }

  switch (rowState.status) {
    case 'downloading':
      return { label: 'Downloading…', tone: 'progress' };
    case 'transcribing':
      return { label: 'Transcribing…', tone: 'progress' };
    case 'saved':
      return {
        label: `Saved${rowState.seconds !== undefined ? ` · ${formatDurationSeconds(rowState.seconds)}` : ''}`,
        tone: 'success',
      };
    case 'failed':
      return { label: 'Failed', secondary: rowState.message, tone: 'error' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'muted' };
    case 'queued':
    default:
      return { label: 'Queued', tone: 'default' };
  }
};

const Transcripts = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Lessons saved during this visit move out of the To-transcribe list into the
  // Review list below; this state is intentionally not persisted across reloads.
  const [reviewEntries, setReviewEntries] = useState<Record<string, ReviewEntry>>({});
  const [activeTab, setActiveTab] = useState<'toTranscribe' | 'review'>('toTranscribe');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [reviewPage, setReviewPage] = useState(0);
  const [reviewRowsPerPage, setReviewRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const searchProps = useFormTextState();
  const reviewModal = useModalState<TranscriptReviewModalState>();

  const transcriber = useTranscriber({ preload: true });
  const {
    stage,
    device,
    modelLoadProgress,
    downloadProgress,
    transcribeProgress,
    prefetchProgress,
    transcribe,
    prefetch,
    cancel,
  } = transcriber;

  const onLessonSaved = (lessonId: string, lrc: string, durationMs?: number) => {
    setLessons((current) => {
      const savedLesson = current.find((lesson) => lesson.id === lessonId);
      if (savedLesson) {
        setReviewEntries((currentEntries) => ({
          ...currentEntries,
          [lessonId]: {
            lesson: { ...savedLesson, lrcTranscript: lrc },
            lrc,
            savedAt: Date.now(),
            durationMs,
          },
        }));
      }
      return current.map((lesson) => (lesson.id === lessonId ? { ...lesson, lrcTranscript: lrc } : lesson));
    });
    setSelectedIds((current) => {
      if (!current.has(lessonId)) {
        return current;
      }
      const next = new Set(current);
      next.delete(lessonId);
      return next;
    });
  };

  const {
    runState,
    rowStates,
    activeLessonId,
    prefetchLessonId,
    activeRowStage,
    run,
    stopAfterCurrent,
    cancelNow,
    resetRowStates,
  } = useBatchTranscription({ transcribe, prefetch, cancel, stage, onLessonSaved });

  const isBatchRunning = runState !== 'idle';
  useBeforeUnloadWarning(isBatchRunning);

  const refreshData = async () => {
    setIsLoading(true);
    const result = await getAllLessons({});
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setLessons(result);
    }
  };

  React.useEffect(() => {
    refreshData().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchValue = searchProps.value?.toLowerCase()?.trim() || '';

  // Reset to the first page whenever the search changes, on both tabs.
  useEffect(() => {
    setPage(0);
    setReviewPage(0);
  }, [searchValue]);

  const filteredLessons = useMemo(() => {
    return lessons
      .filter((lesson) => isTranscriptCandidate(lesson))
      .filter((lesson) => (searchValue ? lesson.title?.toLowerCase()?.includes(searchValue) : true));
  }, [lessons, searchValue]);

  const reviewList = useMemo(() => {
    return Object.values(reviewEntries)
      .filter((entry) => (searchValue ? entry.lesson.title?.toLowerCase()?.includes(searchValue) : true))
      .sort((a, b) => b.savedAt - a.savedAt);
  }, [reviewEntries, searchValue]);

  const selectableIds = useMemo(
    () => new Set(filteredLessons.filter((lesson) => isTranscriptCandidate(lesson)).map((lesson) => lesson.id)),
    [filteredLessons],
  );

  // Keep the current page valid as rows leave the list (e.g. lessons moving to
  // the Review tab after being saved).
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredLessons.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredLessons.length, rowsPerPage, page]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(reviewList.length / reviewRowsPerPage) - 1);
    if (reviewPage > maxPage) {
      setReviewPage(maxPage);
    }
  }, [reviewList.length, reviewRowsPerPage, reviewPage]);

  const pagedLessons = useMemo(
    () => filteredLessons.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredLessons, page, rowsPerPage],
  );

  const pagedReviewList = useMemo(
    () => reviewList.slice(reviewPage * reviewRowsPerPage, reviewPage * reviewRowsPerPage + reviewRowsPerPage),
    [reviewList, reviewPage, reviewRowsPerPage],
  );

  const visibleSelectableIds = useMemo(
    () => pagedLessons.filter((lesson) => selectableIds.has(lesson.id)).map((lesson) => lesson.id),
    [pagedLessons, selectableIds],
  );
  const visibleSelectedCount = visibleSelectableIds.filter((id) => selectedIds.has(id)).length;
  const isPageAllSelected = visibleSelectableIds.length > 0 && visibleSelectedCount === visibleSelectableIds.length;
  const isPageSomeSelected = visibleSelectedCount > 0 && !isPageAllSelected;

  const onToggleRow = (id: string) => {
    if (isBatchRunning) {
      return;
    }
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Toggles selection for the currently visible page only.
  const onTogglePage = () => {
    if (isBatchRunning) {
      return;
    }
    setSelectedIds((current) => {
      const next = new Set(current);
      if (isPageAllSelected) {
        visibleSelectableIds.forEach((id) => next.delete(id));
      } else {
        visibleSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Selects every lesson matching the current search, across all pages.
  const onSelectAll = () => {
    if (isBatchRunning) {
      return;
    }
    setSelectedIds(new Set(selectableIds));
  };

  const onClearSelection = () => {
    if (isBatchRunning) {
      return;
    }
    setSelectedIds(new Set());
  };

  const onChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const onChangeReviewPage = (_event: unknown, newPage: number) => {
    setReviewPage(newPage);
  };

  const onChangeReviewRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReviewRowsPerPage(parseInt(event.target.value));
    setReviewPage(0);
  };

  const onOpenGenerate = (lesson: Lesson) => {
    reviewModal.open({ lesson, autoGenerate: true });
  };

  const onOpenReview = (lesson: Lesson) => {
    reviewModal.open({ lesson, autoGenerate: false });
  };

  const onSaveFromModal = async (lessonId: string, lrc: string): Promise<boolean> => {
    const normalized = normalizeLrc(lrc);
    const result = await updateLesson({ id: lessonId, lrcTranscript: normalized });
    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
      return false;
    }
    onLessonSaved(lessonId, normalized);
    showSuccessToast('Transcript saved.');
    return true;
  };

  const onStartBatch = async () => {
    const selectedLessons = filteredLessons.filter((lesson) => selectedIds.has(lesson.id));
    if (!selectedLessons.length) {
      return;
    }
    resetRowStates(selectedLessons.map((lesson) => lesson.id));
    await run(selectedLessons);
  };

  const onRetryFailed = async () => {
    const failedLessons = filteredLessons.filter(
      (lesson) => selectedIds.has(lesson.id) && rowStates[lesson.id]?.status === 'failed',
    );
    if (!failedLessons.length) {
      return;
    }
    resetRowStates(failedLessons.map((lesson) => lesson.id));
    await run(failedLessons);
  };

  const batchSummary = useMemo(() => {
    const relevant = Array.from(selectedIds)
      .map((id) => rowStates[id])
      .filter(Boolean);
    if (!relevant.length) {
      return undefined;
    }
    const done = relevant.filter((row) => row.status === 'saved').length;
    const failed = relevant.filter((row) => row.status === 'failed').length;
    const savedSeconds = relevant.filter((row) => row.status === 'saved' && row.seconds).map((row) => row.seconds!);
    const avg = savedSeconds.length ? Math.round(savedSeconds.reduce((a, b) => a + b, 0) / savedSeconds.length) : 0;
    // Exclude the currently-active lesson from "remaining": its own in-flight
    // ETA is added separately below instead of being averaged in.
    const remaining = relevant.filter((row) => row.status === 'queued').length;

    const currentLessonEtaSeconds =
      (stage === 'downloading-audio' ? downloadProgress?.etaSeconds : undefined) ??
      (stage === 'transcribing' ? transcribeProgress?.etaSeconds : undefined) ??
      0;

    const remainingEtaSeconds = avg && remaining ? avg * remaining : 0;
    const totalEtaSeconds = remainingEtaSeconds + currentLessonEtaSeconds;
    const eta = totalEtaSeconds > 0 ? Math.round(totalEtaSeconds / 60) : undefined;

    return `${done}/${relevant.length} done${failed ? ` · ${failed} failed` : ''}${avg ? ` · avg ${avg}s/lesson` : ''}${
      eta ? ` · ~${eta} min ETA` : ''
    }`;
  }, [selectedIds, rowStates, stage, downloadProgress, transcribeProgress]);

  const hasFailedSelected = Array.from(selectedIds).some((id) => rowStates[id]?.status === 'failed');
  const selectedCount = Array.from(selectedIds).filter((id) => selectableIds.has(id)).length;
  const needsTranscriptCount = lessons.filter((lesson) => isTranscriptCandidate(lesson)).length;
  const needsTranscriptLabel = `${needsTranscriptCount} lesson${needsTranscriptCount === 1 ? '' : 's'} need${
    needsTranscriptCount === 1 ? 's' : ''
  } transcripts`;

  const activeLessonTitle = useMemo(
    () => (activeLessonId ? lessons.find((lesson) => lesson.id === activeLessonId)?.title : undefined),
    [activeLessonId, lessons],
  );

  const activeLessonProgressText = (() => {
    if (!isBatchRunning || !activeLessonTitle) {
      return undefined;
    }
    if (stage === 'transcribing' && transcribeProgress) {
      return `${activeLessonTitle} · Transcribing ${formatPercent(transcribeProgress.percent)}`;
    }
    if (stage === 'downloading-audio' && downloadProgress) {
      return `${activeLessonTitle} · Downloading ${formatPercent(downloadProgress.percent)}`;
    }
    if (stage === 'loading-model' && modelLoadProgress !== null) {
      return `${activeLessonTitle} · Waiting for model ${formatPercent(modelLoadProgress)}`;
    }
    return `${activeLessonTitle} · ${STAGE_LABELS[stage]}`;
  })();

  const modelStatusText = device
    ? `Model: ready (${device})`
    : modelLoadProgress !== null
    ? `Model: loading ${formatPercent(modelLoadProgress)}`
    : 'Model: loading…';

  return (
    <ProjectContainer title={'Transcripts'} secondaryTitle={needsTranscriptLabel}>
      <Components.Container>
        <Components.StatusBar>
          <Components.StatusText>{modelStatusText}</Components.StatusText>
          {stage !== 'idle' && stage !== 'done' && (
            <Components.StatusText>Stage: {STAGE_LABELS[stage]}</Components.StatusText>
          )}
          {activeLessonProgressText && <Components.StatusText>{activeLessonProgressText}</Components.StatusText>}
        </Components.StatusBar>

        <ProjectTableHeader
          onSearchChange={searchProps.onChangeText}
          searchLabel={'Search by lesson title'}
          searchPlaceholder={'Enter search term'}
        />

        <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)}>
          <Tab value={'toTranscribe'} label={`To transcribe (${filteredLessons.length})`} />
          <Tab value={'review'} label={`Transcribed – review (${reviewList.length})`} />
        </Tabs>

        {activeTab === 'toTranscribe' && (
          <>
            <Components.BatchToolbar>
              <Components.BatchToolbarLeft>
                <Button variant={'text'} size={'small'} disabled={isBatchRunning} onClick={onSelectAll}>
                  Select all
                </Button>
                <Button
                  variant={'text'}
                  size={'small'}
                  disabled={isBatchRunning || !selectedIds.size}
                  onClick={onClearSelection}>
                  Clear selection
                </Button>
                {selectedCount > 0 && (
                  <Components.BatchProgressText>{selectedCount} selected</Components.BatchProgressText>
                )}
              </Components.BatchToolbarLeft>

              <Components.BatchToolbarRight>
                {batchSummary && <Components.BatchProgressText>{batchSummary}</Components.BatchProgressText>}
                {hasFailedSelected && !isBatchRunning && (
                  <Button variant={'outlined'} size={'small'} onClick={onRetryFailed}>
                    Retry failed
                  </Button>
                )}
                {runState === 'running' && (
                  <>
                    <Button variant={'outlined'} size={'small'} onClick={stopAfterCurrent}>
                      Stop after current
                    </Button>
                    <Button variant={'outlined'} color={'error'} size={'small'} onClick={cancelNow}>
                      Cancel now
                    </Button>
                  </>
                )}
                {runState === 'stopping' && (
                  <Components.BatchProgressText>Finishing current lesson…</Components.BatchProgressText>
                )}
                {runState === 'idle' && (
                  <Tooltip title={reviewModal.isModalOpen ? 'Close the review modal before starting a batch run.' : ''}>
                    <span>
                      <Button
                        variant={'contained'}
                        size={'small'}
                        disabled={!selectedCount || reviewModal.isModalOpen}
                        onClick={onStartBatch}>
                        Generate selected ({selectedCount})
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Components.BatchToolbarRight>
            </Components.BatchToolbar>

            <Components.TableWrapper>
              <TableContainer>
                <Table size={'small'}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding={'checkbox'}>
                        <Checkbox
                          size={'small'}
                          checked={isPageAllSelected}
                          indeterminate={isPageSomeSelected}
                          disabled={isBatchRunning || !visibleSelectableIds.length}
                          onChange={onTogglePage}
                        />
                      </TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Type / Group</TableCell>
                      <TableCell>Audio</TableCell>
                      <TableCell>Transcript</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align={'right'}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedLessons.map((lesson) => {
                      const hasTranscript = !!lesson.lrcTranscript?.trim()?.length;
                      const rowState = rowStates[lesson.id];
                      const isActiveRow = activeLessonId === lesson.id;
                      const isPrefetchRow = prefetchLessonId === lesson.id;
                      const display = buildRowDisplay({
                        rowState,
                        isActiveRow,
                        isPrefetchRow,
                        prefetchPercent: prefetchProgress[lesson.audioStreamPath ?? ''],
                        activeStage: isActiveRow ? activeRowStage : undefined,
                        modelLoadProgress,
                        downloadProgress: isActiveRow ? downloadProgress : null,
                        transcribeProgress: isActiveRow ? transcribeProgress : null,
                      });
                      const isSelected = selectedIds.has(lesson.id);

                      return (
                        <TableRow key={lesson.id} selected={isSelected}>
                          <TableCell padding={'checkbox'}>
                            <Checkbox
                              size={'small'}
                              checked={isSelected}
                              disabled={isBatchRunning}
                              onChange={() => onToggleRow(lesson.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Components.RowTitle>{lesson.title}</Components.RowTitle>
                          </TableCell>
                          <TableCell>
                            <Components.RowCaption>{lesson.groupType || lesson.category || '—'}</Components.RowCaption>
                          </TableCell>
                          <TableCell>Yes</TableCell>
                          <TableCell>
                            {hasTranscript ? `Yes (${lineCount(lesson.lrcTranscript)} lines)` : 'No'}
                          </TableCell>
                          <TableCell>
                            {display ? (
                              <Components.StatusCellContent>
                                <Components.StatusChip tone={display.tone}>{display.label}</Components.StatusChip>
                                {display.secondary && (
                                  <Components.RowCaption>{display.secondary}</Components.RowCaption>
                                )}
                                {display.progressPercent !== undefined && (
                                  <LinearProgress
                                    sx={{ width: '100%', minWidth: 80 }}
                                    variant={'determinate'}
                                    value={display.progressPercent}
                                  />
                                )}
                              </Components.StatusCellContent>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align={'right'}>
                            <Button size={'small'} disabled={isBatchRunning} onClick={() => onOpenGenerate(lesson)}>
                              Generate
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!filteredLessons.length && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Components.EmptyState>
                            <Typography variant={'body2'}>
                              All daily lessons with audio already have transcripts.
                            </Typography>
                          </Components.EmptyState>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!!filteredLessons.length && (
                <TablePagination
                  component={'div'}
                  count={filteredLessons.length}
                  page={page}
                  onPageChange={onChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
              )}
            </Components.TableWrapper>
          </>
        )}

        {activeTab === 'review' && (
          <Components.TableWrapper>
            <Components.BatchToolbar>
              <Components.BatchProgressText>
                Lessons transcribed during this visit. Reload clears this list.
              </Components.BatchProgressText>
            </Components.BatchToolbar>
            <TableContainer>
              <Table size={'small'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Lines</TableCell>
                    <TableCell>Time taken</TableCell>
                    <TableCell>Saved at</TableCell>
                    <TableCell align={'right'}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedReviewList.map((entry) => (
                    <TableRow key={entry.lesson.id}>
                      <TableCell>
                        <Components.RowTitle>{entry.lesson.title}</Components.RowTitle>
                      </TableCell>
                      <TableCell>{lineCount(entry.lrc)}</TableCell>
                      <TableCell>
                        {entry.durationMs !== undefined ? formatDurationSeconds(entry.durationMs / 1000) : '—'}
                      </TableCell>
                      <TableCell>{new Date(entry.savedAt).toLocaleTimeString()}</TableCell>
                      <TableCell align={'right'}>
                        <Button size={'small'} disabled={isBatchRunning} onClick={() => onOpenReview(entry.lesson)}>
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!reviewList.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Components.EmptyState>
                          <Typography variant={'body2'}>No transcripts saved yet during this visit.</Typography>
                        </Components.EmptyState>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!!reviewList.length && (
              <TablePagination
                component={'div'}
                count={reviewList.length}
                page={reviewPage}
                onPageChange={onChangeReviewPage}
                rowsPerPage={reviewRowsPerPage}
                onRowsPerPageChange={onChangeReviewRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              />
            )}
          </Components.TableWrapper>
        )}

        {stage === 'error' && <Alert severity={'error'}>Transcription error. Check the row status for details.</Alert>}
        {isBatchRunning && <LinearProgress sx={{ width: '100%' }} />}
      </Components.Container>

      <TranscriptReviewModal
        isModalOpen={reviewModal.isModalOpen}
        onModalClose={reviewModal.onModalClose}
        modalState={reviewModal.modalState}
        onSave={onSaveFromModal}
        disabled={isBatchRunning}
        disabledReason={isBatchRunning ? 'A batch run is in progress. Wait for it to finish or cancel it.' : undefined}
        transcriber={transcriber}
      />
    </ProjectContainer>
  );
};

export default Transcripts;
